import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

const categoryIcons: Record<string, string> = {
  "Shoes": "ri-footprint-line",
  "Hoodies/Sweaters": "ri-t-shirt-line",
  "T-Shirts": "ri-t-shirt-line",
  "Jackets": "ri-shirt-line",
  "Pants/Shorts": "ri-run-line",
  "Headwear": "ri-user-smile-line",
  "Sets": "ri-stack-line",
  "Underwear/Underpants": "ri-shirt-line",
  "Jersey": "ri-t-shirt-line",
  "Accessories": "ri-star-line",
  "Bags": "ri-handbag-line",
  "Belts": "ri-loop-left-line",
  "Perfumes": "ri-drop-line",
  "Other Stuff": "ri-more-line",
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const guideArticles = [
    { label: "Sugargoo Spreadsheet Guide", path: "/blog/sugargoo-spreadsheet-complete-guide" },
    { label: "Sugargoo Review 2026", path: "/blog/sugargoo-review-2026" },
    { label: "Shipping Cost Guide", path: "/blog/sugargoo-shipping-cost-guide" },
    { label: "Coupon & Discount Guide", path: "/blog/sugargoo-coupon-discount-guide" },
    { label: "Warehouse Guide", path: "/blog/sugargoo-warehouse-guide" },
    { label: "Is Sugargoo Legit?", path: "/blog/is-sugargoo-legit" },
    { label: "For Beginners", path: "/blog/sugargoo-spreadsheet-for-beginners" },
    { label: "Find Winning Products", path: "/blog/how-to-find-winning-products" },
    { label: "For Dropshipping", path: "/blog/sugargoo-spreadsheet-for-dropshipping" },
    { label: "China Shopping Tips", path: "/blog/china-shopping-tips" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200 flex-shrink-0"
          >
            {isHome ? (
              <h1 className="font-heading text-xl md:text-2xl font-bold tracking-tight m-0">
                Sugargoo<span className="text-primary-500">Sheet</span>
              </h1>
            ) : (
              <span className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                Sugargoo<span className="text-primary-500">Sheet</span>
              </span>
            )}
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1"></i>Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 animate-[fadeIn_0.2s_ease-out] z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${categoryIcons[cat.name] || "ri-store-line"} text-foreground-400 text-sm w-4 flex-shrink-0`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                <span>Shopping Guide</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 animate-[fadeIn_0.2s_ease-out] z-50">
                  <Link
                    to="/shopping-guide"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-primary-500 hover:bg-primary-50 border-b border-background-200/70 transition-colors duration-150"
                  >
                    <i className="ri-compass-3-line"></i> View All Guides
                  </Link>
                  {guideArticles.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      className="block px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                    >
                      {article.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50">
                <i className="ri-home-4-line text-primary-500"></i>
              </div>
              <span>Home</span>
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50">
                <i className="ri-shopping-cart-2-line text-primary-500"></i>
              </div>
              <span>Sugargoo Spreadsheet</span>
            </a>

            <div className="border-t border-background-200/70 my-2"></div>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <span className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50">
                  <i className="ri-grid-line text-accent-500"></i>
                </div>
                Product Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-accent-200 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 rounded-lg transition-colors duration-150"
                  >
                    <i className={`${categoryIcons[cat.name] || "ri-store-line"} text-foreground-400 text-sm w-4 flex-shrink-0`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150"
            >
              <span className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50">
                  <i className="ri-book-open-line text-secondary-500"></i>
                </div>
                Shopping Guide
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileGuideOpen && (
              <div className="ml-4 pl-4 border-l-2 border-secondary-200 space-y-0.5">
                <Link
                  to="/shopping-guide"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm font-semibold text-primary-500 hover:bg-primary-50 rounded-lg transition-colors duration-150"
                >
                  View All Guides
                </Link>
                {guideArticles.map((article) => (
                  <Link
                    key={article.path}
                    to={article.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 rounded-lg transition-colors duration-150"
                  >
                    {article.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-background-200/70">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}